import { useMemo, useState } from 'react';
import { X, ClipboardPaste, Check } from 'lucide-react';
import {
  parsearTextoColadoExcel,
  ROTULOS_COLUNA_EXCEL,
  type TipoColunaExcel,
  type LinhaParseadaExcel,
} from '../../utils/colarExcelRelatorio';

export interface RelatorioItemNovo {
  nomePeca: string;
  quantidade: number;
  medidaMenor: number;
  medidaMaior: number;
  material: 'mdf' | 'aglomerado';
  espessura: number;
}

interface Props {
  aberto: boolean;
  onFechar: () => void;
  onImportar: (linhas: RelatorioItemNovo[]) => void;
  proximoCorteNumero: number;
}

export default function ColarExcelRelatorioModal({
  aberto,
  onFechar,
  onImportar,
}: Props) {
  const [texto, setTexto] = useState('');
  const [mapaManual, setMapaManual] = useState<TipoColunaExcel[] | null>(null);

  const resultado = useMemo(() => {
    if (!texto.trim()) return null;
    return parsearTextoColadoExcel(texto, mapaManual ?? undefined);
  }, [texto, mapaManual]);

  const numCols = useMemo(() => {
    if (!resultado?.cabecalhosBrutos.length) {
      const primeira = texto.trim().split(/\r?\n/)[0];
      if (!primeira) return 0;
      return primeira.includes('\t')
        ? primeira.split('\t').length
        : primeira.split(';').length || 1;
    }
    return resultado.cabecalhosBrutos.length;
  }, [texto, resultado]);

  const mapaAtual = useMemo((): TipoColunaExcel[] => {
    if (mapaManual && mapaManual.length >= numCols) return mapaManual.slice(0, numCols);
    if (resultado?.mapaColunas.length) return resultado.mapaColunas;
    return Array.from({ length: numCols }, () => 'ignorar' as TipoColunaExcel);
  }, [mapaManual, numCols, resultado]);

  if (!aberto) return null;

  const confirmar = () => {
    if (!resultado || resultado.linhas.length === 0) return;
    onImportar(resultado.linhas);
    setTexto('');
    setMapaManual(null);
    onFechar();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between border-b px-4 py-3 bg-[#1F4E78] text-white">
          <h3 className="font-semibold flex items-center gap-2">
            <ClipboardPaste className="w-5 h-5" />
            Colar do Excel
          </h3>
          <button type="button" onClick={onFechar} className="p-1 hover:bg-white/20 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          <p className="text-sm text-neutral-600">
            No Excel, selecione as células (com cabeçalho se tiver), copie com{' '}
            <strong>Ctrl+C</strong> e cole abaixo com <strong>Ctrl+V</strong>.
          </p>
          <textarea
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              setMapaManual(null);
            }}
            placeholder="Cole aqui (Ctrl+V)..."
            className="w-full h-32 font-mono text-sm border rounded-lg p-3 border-[#009b3a]/30"
          />

          {numCols > 0 && (
            <div className="rounded-lg border border-neutral-200 p-3 bg-neutral-50">
              <p className="text-xs font-semibold text-neutral-700 mb-2">
                Mapeamento das colunas ({numCols} colunas detectadas)
              </p>
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: numCols }, (_, idx) => (
                  <div key={idx} className="flex flex-col gap-1">
                    <span className="text-[10px] text-neutral-500">Col. {idx + 1}</span>
                    <select
                      value={mapaAtual[idx] || 'ignorar'}
                      onChange={(e) => {
                        const v = e.target.value as TipoColunaExcel;
                        setMapaManual((prev) => {
                          const base = prev ? [...prev] : [...mapaAtual];
                          while (base.length < numCols) base.push('ignorar');
                          base[idx] = v;
                          return base;
                        });
                        if (texto.trim()) {
                          setMapaManual((m) => m || [...mapaAtual]);
                        }
                      }}
                      className="text-xs border rounded px-2 py-1 bg-white"
                    >
                      {(Object.keys(ROTULOS_COLUNA_EXCEL) as TipoColunaExcel[]).map((k) => (
                        <option key={k} value={k}>
                          {ROTULOS_COLUNA_EXCEL[k]}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {resultado && (
            <>
              {resultado.avisos.length > 0 && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  {resultado.avisos.map((a, i) => (
                    <div key={i}>{a}</div>
                  ))}
                </div>
              )}
              <p className="text-sm font-medium text-[#009b3a]">
                {resultado.linhas.length} linha(s) pronta(s) para importar
              </p>
              {resultado.linhas.length > 0 && (
                <div className="overflow-x-auto max-h-48 border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-neutral-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">Peça</th>
                        <th className="px-2 py-1">Qtd</th>
                        <th className="px-2 py-1">Medida</th>
                        <th className="px-2 py-1">Mat.</th>
                        <th className="px-2 py-1">Esp.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultado.linhas.slice(0, 30).map((l, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1">{l.nomePeca || '—'}</td>
                          <td className="px-2 py-1 text-center">{l.quantidade}</td>
                          <td className="px-2 py-1 text-center font-mono">
                            {l.medidaMenor}×{l.medidaMaior}
                          </td>
                          <td className="px-2 py-1 text-center uppercase">
                            {l.material === 'mdf' ? 'MDF' : 'AGLO'}
                          </td>
                          <td className="px-2 py-1 text-center">{l.espessura}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {resultado.linhas.length > 30 && (
                    <p className="text-xs text-neutral-500 p-2">
                      … e mais {resultado.linhas.length - 30} linhas
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t p-4 bg-neutral-50">
          <button
            type="button"
            onClick={onFechar}
            className="px-4 py-2 rounded-lg border text-sm font-medium"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!resultado || resultado.linhas.length === 0}
            onClick={confirmar}
            className="px-4 py-2 rounded-lg bg-[#009b3a] text-white text-sm font-semibold disabled:opacity-40 flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            Importar {resultado?.linhas.length ?? 0} linha(s)
          </button>
        </div>
      </div>
    </div>
  );
}
